import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { showsChips } from '@/cowork/omniad/noChips';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Send, Loader2, RotateCcw, Download, Bookmark, Check, CheckCircle2, Circle, Mic, MicOff, Volume2, VolumeX, ClipboardList, PhoneOff, ThumbsUp, ThumbsDown, MessageCircle, Paperclip, X, FileText, Image as ImageIcon, Music, ArrowRight } from 'lucide-react';
import type { Agent } from '../types/cowork';
import { cn } from '@/components/ui/utils';
import { AgentAvatar } from './AgentAvatar';
import { ContentCard, type ContentCardData } from './ContentCard';
import { PresentationModal } from './PresentationModal';
import { coworkApi, API_BASE_URL, authHeaders } from '../api/coworkApi';
import { plainTagline, plainTitle } from '../plainDashes';


interface GeneratedImage {
  contentId: string;
  imageUrl: string;
  title: string;
  platform: string;
}

interface TaskCreatedInfo {
  taskId: string;
  title: string;
  description?: string;
  assignedAgentId?: string;
  assignedAgentName?: string;
  assignedAgentEmoji?: string;
  agentId?: string;
  agentName?: string;
  priority?: string;
  deadline?: string | null;
  status?: string;
  deliverableUrl?: string;
  deliverableType?: string;
  deliverableTitle?: string;
}

interface GeneratedDocument {
  contentId: string;
  /** New: HTML presentation flow. When present, click "Open" to launch the modal viewer. */
  presentationId?: string;
  /** Legacy: file-on-disk download URL (xlsx/docx, or pre-rollout pptx). */
  documentUrl?: string;
  title: string;
  format: string;
}

interface DashboardCreatedInfo {
  dashboardId: string;
  title: string;
  type: string;
}

const DOC_ICONS: Record<string, string> = {
  pptx: '📊',
  xlsx: '📈',
  docx: '📝',
};

const DOC_LABELS: Record<string, string> = {
  pptx: 'PowerPoint',
  xlsx: 'Excel',
  docx: 'Word',
};

// Memoized markdown renderer for completed messages — avoids re-parsing all
// markdown on every typewriter tick of the streaming message.
const MemoMarkdown = memo(({ content }: { content: string }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      a: ({ href, children, ...props }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      ),
    }}
  >{content}</ReactMarkdown>
));


interface ToolActivity {
  tool: string;
  label: string;
  input?: string;
  status: 'running' | 'done' | 'error';
}

// Dynamic thinking indicator — cycles through contextual status messages
const THINKING_PHASES = [
  'Thinking...',
  'Analyzing your request...',
  'Reviewing context...',
  'Preparing response...',
  'Processing...',
  'Working on it...',
  'Almost there...',
];

// ─── Feedback Buttons (👍/👎 + optional comment) ────────────────

function FeedbackButtons({ agentId, messageContent, messageIndex, accentColor }: {
  agentId: string;
  messageContent: string;
  messageIndex: number;
  accentColor: string;
}) {
  const [reaction, setReaction] = useState<'positive' | 'negative' | null>(null);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const doSubmit = (r: 'positive' | 'negative', c?: string) => {
    setReaction(r);
    setSubmitted(true);
    setShowComment(false);
    coworkApi.feedback.submit(agentId, {
      reaction: r,
      comment: c || undefined,
      contextType: 'chat',
      contextContent: messageContent.slice(0, 500),
      messageId: `msg-${messageIndex}`,
    }).catch(() => {});
  };

  if (submitted) {
    return (
      <div className="flex items-center gap-1 mt-1.5 opacity-60">
        <span className="text-[10px] text-[#98a2b3]">
          {reaction === 'positive' ? '👍' : '👎'} Feedback saved
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-0.5 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        onClick={() => doSubmit('positive')}
        className="w-6 h-6 flex items-center justify-center rounded-md text-[#c4c9d4] hover:text-green-500 hover:bg-green-50 transition-all"
        title="Good response"
      >
        <ThumbsUp className="w-3 h-3" />
      </button>
      <button
        onClick={() => {
          setReaction('negative');
          setShowComment(true);
        }}
        className="w-6 h-6 flex items-center justify-center rounded-md text-[#c4c9d4] hover:text-red-400 hover:bg-red-50 transition-all"
        title="Needs improvement"
      >
        <ThumbsDown className="w-3 h-3" />
      </button>
      <button
        onClick={() => setShowComment(prev => !prev)}
        className="w-6 h-6 flex items-center justify-center rounded-md text-[#c4c9d4] hover:text-[#667085] hover:bg-gray-50 transition-all"
        title="Add comment"
      >
        <MessageCircle className="w-3 h-3" />
      </button>

      {showComment && (
        <div className="flex items-center gap-1.5 ml-1">
          <input
            type="text"
            value={comment}
            onChange={e => setComment(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && comment.trim()) {
                doSubmit(reaction ?? 'negative', comment.trim());
              }
            }}
            placeholder="What could be better?"
            autoFocus
            className="text-[11px] px-2 py-1 rounded-md border border-gray-200 bg-white outline-none focus:border-gray-300 w-[200px]"
          />
          <button
            onClick={() => {
              if (comment.trim()) doSubmit(reaction ?? 'negative', comment.trim());
              else doSubmit(reaction ?? 'negative');
            }}
            className="text-[10px] px-2 py-1 rounded-md text-white"
            style={{ background: accentColor, fontWeight: 600 }}
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}

// ── Voice thinking ambient sound ──────────────────────────────
// Plays a soft ambient MP3 loop while the agent thinks deeply
function createThinkingSound(): { start: () => void; stop: () => void } {
  let audio: HTMLAudioElement | null = null;
  let fadeInterval: ReturnType<typeof setInterval> | null = null;

  return {
    start: () => {
      try {
        audio = new Audio(`${API_BASE_URL}/audio/system/thinking-ambient.mp3`);
        audio.loop = true;
        audio.volume = 0;
        audio.play().then(() => {
          // Fade in over 500ms
          let vol = 0;
          fadeInterval = setInterval(() => {
            vol = Math.min(vol + 0.02, 0.15); // Max volume 15% — subtle background
            if (audio) audio.volume = vol;
            if (vol >= 0.15 && fadeInterval) clearInterval(fadeInterval);
          }, 30);
        }).catch(() => { /* Autoplay blocked — silent fallback */ });
      } catch { /* Audio not available */ }
    },
    stop: () => {
      if (fadeInterval) { clearInterval(fadeInterval); fadeInterval = null; }
      if (!audio) return;
      // Fade out over 300ms
      const a = audio;
      let vol = a.volume;
      const fade = setInterval(() => {
        vol = Math.max(vol - 0.03, 0);
        a.volume = vol;
        if (vol <= 0) {
          clearInterval(fade);
          a.pause();
          a.src = '';
        }
      }, 20);
      audio = null;
    },
  };
}

function ThinkingIndicator({ color, agentName }: { color: string; agentName?: string }) {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [dots, setDots] = useState('');

  useEffect(() => {
    // Cycle through phases every 3 seconds
    const phaseTimer = setInterval(() => {
      setPhaseIdx(prev => (prev + 1) % THINKING_PHASES.length);
    }, 3000);
    return () => clearInterval(phaseTimer);
  }, []);

  useEffect(() => {
    // Animate dots every 500ms
    const dotTimer = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(dotTimer);
  }, []);

  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <div className="relative w-5 h-5 shrink-0">
        {/* Spinning ring */}
        <div
          className="absolute inset-0 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: `${color}30`, borderTopColor: color }}
        />
        {/* Inner dot */}
        <div className="absolute inset-[5px] rounded-full animate-pulse" style={{ background: color }} />
      </div>
      <span className="text-[12px] transition-all duration-300" style={{ color, fontWeight: 500 }}>
        {THINKING_PHASES[phaseIdx]}{dots}
      </span>
    </div>
  );
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
  fromVoice?: boolean;
  /** Tool activity label shown while streaming (e.g. "Generating visual…") */
  toolLabel?: string;
  /** Rich tool activity trace */
  toolActivities?: ToolActivity[];
  /** Which agent sent this (for multi-agent call mode) */
  agentId?: string;
  agentName?: string;
  agentEmoji?: string;
  agentColor?: string;
}

// ─── SpeechRecognition types ──────────────────────────────────────

interface SpeechRecognitionEvent {
  resultIndex: number;
  results: { length: number; [index: number]: { isFinal: boolean; [index: number]: { transcript: string } } };
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: { error: string }) => void) | null;
}

declare global {
  interface Window {
    SpeechRecognition?: new () => SpeechRecognitionInstance;
    webkitSpeechRecognition?: new () => SpeechRecognitionInstance;
  }
}

// Suggested prompts per agent
const AGENT_PROMPTS: Record<string, string[]> = {
  'maya-seo': [
    'Run a full SEO audit on our website',
    'Check our rankings for our top keywords',
    'Find keyword opportunities we\'re missing',
  ],
  'leo-perf': [
    'Where is my ad spend being wasted?',
    'Review my Google Ads campaign structure',
    'What should I scale vs. pause this week?',
  ],
  'aria-content': [
    'Write a LinkedIn post about our latest product',
    'Help me build a content calendar for Q2',
    'Rewrite my homepage headline',
  ],
  'rex-influencer': [
    'Find influencers in the beauty space under 100k',
    'Is this creator a good fit for our brand?',
    'Draft an influencer outreach message',
  ],
  'nova-strategy': [
    'Help me sharpen our brand positioning',
    'Do a quick competitor teardown',
    'What\'s our key differentiator?',
  ],
  'kai-analytics': [
    'What metrics should I be tracking weekly?',
    'Help me set up a GA4 dashboard',
    'Explain my conversion funnel drop-offs',
  ],
  'jones-artdir': [
    'Create a LinkedIn banner for our product launch',
    'Design an Instagram visual for our latest campaign',
    'Generate a set of social media visuals for Q2',
  ],
};

const DEFAULT_PROMPTS = [
  'What can you help me with?',
  'Give me your honest take on our marketing',
  'Where should I focus first?',
];

interface AgentChatPageProps {
  agent: Agent;
  onBack: () => void;
  onContentSaved?: () => void;
  onNavigateToTasks?: (taskId?: string) => void;
  /** Multi-agent call mode: pass all agents in the call */
  callAgents?: Agent[];
  /** Auto-start voice when entering call mode */
  autoVoice?: boolean;
  /** True for a live voice call (vs a multi-agent text chat) */
  voiceCall?: boolean;
  /** Active brand ID for brand-scoped agent memory */
  brandId?: string;
  /** Navigate to a specific dashboard */
  onViewDashboard?: (dashboardId: string) => void;
  /** Auto-send this message when the chat opens */
  initialMessage?: string;
  /** Restore this specific chat session instead of the agent's most recent one */
  sessionId?: string;
}

/**
 * Map a raw streaming/API error to a message that's safe and useful for a
 * marketing user. Never surface provider names, billing internals, HTTP
 * status codes, or vendor doc links in the chat transcript.
 */
function friendlyChatError(err: unknown): string {
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase();
  if (/429|quota|rate limit|too many requests/.test(msg)) {
    return "I'm a bit overloaded right now: give me a moment and try again.";
  }
  if (/401|403|unauthor|invalid.*key|api key/.test(msg)) {
    return 'I hit a configuration issue reaching the AI service. Please try again shortly.';
  }
  if (/timeout|timed out|econnreset|network|fetch failed|503|502|504/.test(msg)) {
    return 'The connection dropped before I could finish. Please try again.';
  }
  return "Something went wrong on my end: please try that again.";
}

export function AgentChatPage({ agent: initialAgent, onBack, onContentSaved, onNavigateToTasks, callAgents, autoVoice, voiceCall, brandId, onViewDashboard, initialMessage, sessionId }: AgentChatPageProps) {
  // Two independent axes that used to be conflated:
  //  - isMultiAgent: more than one agent in the room → multi-agent routing,
  //    per-message attribution, and the participant sidebar (text OR voice).
  //  - isVoiceCall: this is a live voice call → auto-voice, the Live badge,
  //    call timer, End button, TTS prefill, waveform.
  // Group Chat = multi-agent, NOT a voice call. Group Call = both.
  const isMultiAgent = !!callAgents && callAgents.length > 1;
  const isVoiceCall = !!voiceCall;
  // Layout (sidebar, avatar stack, message attribution) keys off multi-agent.
  const isCallMode = isMultiAgent;
  const [activeAgent, setActiveAgent] = useState<Agent>(initialAgent);
  const agent = activeAgent;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<Array<{ file: File; preview?: string; type: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [suggestedReplies, setSuggestedReplies] = useState<string[]>([]);
  const [contentCards, setContentCards] = useState<ContentCardData[]>([]);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [generatedDocuments, setGeneratedDocuments] = useState<GeneratedDocument[]>([]);
  const [openPresentationId, setOpenPresentationId] = useState<string | null>(null);
  const [createdTasks, setCreatedTasks] = useState<TaskCreatedInfo[]>([]);
  const [createdDashboards, setCreatedDashboards] = useState<DashboardCreatedInfo[]>([]);
  const [approvalCards, setApprovalCards] = useState<Array<{
    approvalId: string; type: string; title: string; details?: string;
    resolvedStatus?: 'approved' | 'rejected' | null;
  }>>([]);
  const [okrs, setOkrs] = useState<Array<{ id: string; title: string; progress: number }>>([]);
  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [restoringHistory, setRestoringHistory] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Poll for task status updates (TMS-routed tasks)
  useEffect(() => {
    const activeTasks = createdTasks.filter(t => t.status && !['done', 'failed', 'stopped'].includes(t.status));
    if (activeTasks.length === 0) return;

    const interval = setInterval(async () => {
      if (document.hidden) return; // skip polling in hidden tabs
      const results = await Promise.all(
        activeTasks.map(task =>
          coworkApi.tasks.get(task.taskId)
            .then(result => ({ taskId: task.taskId, result }))
            .catch(() => null) /* ignore polling errors */
        )
      );

      setCreatedTasks(prev => {
        let changed = false;
        const next = prev.map(t => {
          const hit = results.find(r => r !== null && r.taskId === t.taskId);
          if (!hit) return t;
          const status = (hit.result.task as unknown as Record<string, unknown>).status as string;
          if (status === t.status) return t; // keep same reference when unchanged
          const deliverables = hit.result.deliverables || [];
          changed = true;
          const updated = { ...t, status };
          if (status === 'done' && deliverables.length > 0) {
            const d = deliverables[0];
            updated.deliverableUrl = d.content;
            updated.deliverableType = d.type;
            try { updated.deliverableTitle = (typeof d.metadata === 'string' ? JSON.parse(d.metadata) as Record<string, string> : d.metadata as Record<string, string>)?.title || t.title; } catch { updated.deliverableTitle = t.title; }
          }
          return updated;
        });
        return changed ? next : prev; // stable array identity when nothing changed
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [createdTasks]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Restore a chat session on mount: the one passed via `sessionId`, or the
  // most recent session for this agent when no sessionId is provided.
  useEffect(() => {
    let cancelled = false;
    setRestoringHistory(true);
    const sessionPromise: Promise<string | null> = sessionId
      ? Promise.resolve(sessionId)
      : coworkApi.chats.list().then(data => data.sessions.find(s => s.agentId === agent.id)?.id ?? null);
    sessionPromise.then(resolvedSessionId => {
      if (cancelled) return;
      if (resolvedSessionId) {
        setChatSessionId(resolvedSessionId);
        // Load messages, tasks, and presentations in parallel — so deliverable
        // cards (Open Presentation, Task complete) survive page refreshes and
        // tab navigation. Without this, the in-memory createdTasks /
        // generatedDocuments state is empty after a reload.
        Promise.all([
          coworkApi.chats.get(agent.id, resolvedSessionId).catch(() => null),
          coworkApi.tasks.list(undefined, undefined, { chatSessionId: resolvedSessionId }).catch(() => ({ tasks: [] as any[] })),
          coworkApi.presentations.list({ sessionId: resolvedSessionId }).catch(() => ({ presentations: [] as any[] })),
        ]).then(([chatData, tasksData, presData]) => {
          if (cancelled) return;
          if (chatData) {
            const restored: ChatMessage[] = chatData.messages.map(m => ({
              role: m.role as 'user' | 'assistant',
              content: m.content,
            }));
            // Don't clobber messages the user already sent during the restore window
            if (restored.length > 0) setMessages(prev => prev.length > 0 ? prev : restored);
          }

          // Restore task cards (one per task that ran in this chat session).
          // Fetch each task's deliverables so the "Open Presentation" /
          // "Download" button reappears in the same form as live polling.
          const tasks = tasksData?.tasks ?? [];
          if (tasks.length > 0) {
            Promise.all(
              tasks.map((t: any) =>
                coworkApi.tasks.get(t.id).then((td) => {
                  const d = (td.deliverables ?? [])[0];
                  let deliverableTitle: string | undefined;
                  if (d) {
                    try {
                      const meta = typeof d.metadata === 'string' ? JSON.parse(d.metadata) : d.metadata;
                      deliverableTitle = (meta as Record<string, string> | undefined)?.title;
                    } catch { /* ignore parse error */ }
                  }
                  return {
                    taskId: t.id,
                    title: t.title,
                    description: t.description,
                    assignedAgentId: t.assignedAgentId,
                    priority: t.priority,
                    deadline: t.deadline,
                    status: t.status,
                    deliverableUrl: d?.content,
                    deliverableType: d?.type,
                    deliverableTitle: deliverableTitle ?? t.title,
                  } as TaskCreatedInfo;
                }).catch(() => ({
                  taskId: t.id,
                  title: t.title,
                  status: t.status,
                } as TaskCreatedInfo))
              )
            ).then((restoredTasks) => {
              if (!cancelled) setCreatedTasks(restoredTasks.filter(Boolean));
            });
          }

          // Restore generatedDocuments panel from presentations created in
          // this chat session. (Direct generate_document calls land here;
          // task-routed ones land in createdTasks via the block above.)
          const presentations = presData?.presentations ?? [];
          if (presentations.length > 0) {
            const taskSourceIds = new Set(tasks.map((t: any) => String(t.id)));
            const docs: GeneratedDocument[] = presentations
              .filter((p) => !taskSourceIds.has(String(p.sourceId)))
              .map((p) => ({
                contentId: p.id,
                presentationId: p.id,
                title: p.title,
                format: 'pptx',
              }));
            if (docs.length > 0) setGeneratedDocuments(docs);
          }

          setRestoringHistory(false);
        }).catch(() => setRestoringHistory(false));
      } else {
        setRestoringHistory(false);
      }
    }).catch(() => setRestoringHistory(false));
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.id, sessionId]);

  // Load OKRs for the account-manager agent
  useEffect(() => {
    if (agent.id === 'account-manager') {
      coworkApi.okrs.list(brandId).then(data => {
        setOkrs((data.okrs || []).map(o => ({
          id: o.id,
          title: o.title,
          progress: o.progress ?? 0,
        })));
      }).catch(() => {});
    }
  }, [agent.id, brandId]);

  // ─── Voice state ────────────────────────────────────────────────
  const [voiceMode, setVoiceMode] = useState(false);
  const voiceModeRef = useRef(false);
  // Keep ref in sync with state so async functions always see current value
  useEffect(() => { voiceModeRef.current = voiceMode; }, [voiceMode]);
  const [isListening, setIsListening] = useState(false);
  const [interimText, setInterimText] = useState('');
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const isPlayingAudioRef = useRef(false);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const userWantsMicRef = useRef(false);
  // Ref-based loading tracker for voice callbacks (avoids stale closure)
  const loadingRef = useRef(false);
  const pendingVoiceRef = useRef<string | null>(null);

  // ─── Filler audio cache (call mode) ──────────────────────────
  const fillerCacheRef = useRef<Record<string, { question: HTMLAudioElement[]; statement: HTMLAudioElement[]; generic: HTMLAudioElement[] }>>({});
  const fillerReadyRef = useRef(false);

  // Pre-fetch filler audio clips when call starts
  useEffect(() => {
    if (!isVoiceCall || !callAgents?.length) return;
    let cancelled = false;

    coworkApi.tts.prefill(callAgents.map(a => a.id)).then(({ fillers }) => {
      if (cancelled) return;
      const cache: typeof fillerCacheRef.current = {};
      for (const [agentId, urls] of Object.entries(fillers)) {
        const preload = (urlList: string[]) =>
          urlList.map(url => {
            const audio = new Audio(coworkApi.tts.audioSrc(url));
            audio.preload = 'auto';
            audio.load();
            return audio;
          });
        cache[agentId] = {
          question: preload(urls.question),
          statement: preload(urls.statement),
          generic: preload(urls.generic),
        };
      }
      fillerCacheRef.current = cache;
      fillerReadyRef.current = true;
      console.log('[fillers] Pre-cached filler audio for', Object.keys(cache).join(', '));
    }).catch(err => {
      console.warn('[fillers] Failed to prefetch fillers:', err);
    });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVoiceCall]);

  /** Play a random filler clip for an agent. Returns a promise that resolves when the filler finishes. */
  const playFiller = useCallback((agentId: string, utterance: string): Promise<void> => {
    if (!fillerReadyRef.current) return Promise.resolve();
    const agentFillers = fillerCacheRef.current[agentId];
    if (!agentFillers) return Promise.resolve();

    // Pick category based on utterance
    const isQuestion = /\?$/.test(utterance.trim());
    const pool = isQuestion ? agentFillers.question : agentFillers.generic;
    if (!pool.length) return Promise.resolve();

    const clip = pool[Math.floor(Math.random() * pool.length)];

    return new Promise<void>((resolve) => {
      // Clone the audio node so we can replay the same clip
      const audio = clip.cloneNode(true) as HTMLAudioElement;
      isPlayingAudioRef.current = true;
      currentAudioRef.current = audio;
      pauseRecognition();

      audio.onended = () => {
        isPlayingAudioRef.current = false;
        currentAudioRef.current = null;
        resolve();
      };
      audio.onerror = () => {
        isPlayingAudioRef.current = false;
        currentAudioRef.current = null;
        resolve();
      };
      audio.play().catch(() => resolve());
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const noChips = !showsChips(agent.id);
  const prompts = noChips ? [] : AGENT_PROMPTS[agent.id] ?? DEFAULT_PROMPTS;

  // Auto-start voice mode for call mode
  useEffect(() => {
    if (autoVoice && !voiceMode) {
      setVoiceMode(true);
      userWantsMicRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoVoice]);

  // Sync activeAgent if initialAgent changes
  useEffect(() => {
    setActiveAgent(initialAgent);
  }, [initialAgent]);

  // Call timer
  const [callStartTime] = useState(() => isVoiceCall ? Date.now() : 0);
  const [callTimer, setCallTimer] = useState(0);
  useEffect(() => {
    if (!isVoiceCall) return;
    const interval = setInterval(() => {
      setCallTimer(Math.floor((Date.now() - callStartTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [isVoiceCall, callStartTime]);
  const callMM = String(Math.floor(callTimer / 60)).padStart(2, '0');
  const callSS = String(callTimer % 60).padStart(2, '0');

  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    // Smooth-scroll only when a new message arrives; during typewriter ticks
    // (same message count) jump instantly so smooth animations don't fight.
    const behavior: ScrollBehavior = messages.length > prevMessageCountRef.current ? 'smooth' : 'auto';
    prevMessageCountRef.current = messages.length;
    bottomRef.current?.scrollIntoView({ behavior });
  }, [messages, interimText]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [input]);

  // ─── TTS playback ──────────────────────────────────────────────

  const playAgentAudio = useCallback(async (text: string, agentIdOverride?: string) => {
    // Pause mic while speaking
    pauseRecognition();

    try {
      const { audioUrl } = await coworkApi.tts.generate(agentIdOverride ?? agent.id, text);
      const audio = new Audio(coworkApi.tts.audioSrc(audioUrl));
      currentAudioRef.current = audio;
      isPlayingAudioRef.current = true;

      audio.onended = () => {
        isPlayingAudioRef.current = false;
        currentAudioRef.current = null;
        if (userWantsMicRef.current) {
          setTimeout(() => resumeRecognition(), 300);
        }
      };

      audio.onerror = () => {
        isPlayingAudioRef.current = false;
        currentAudioRef.current = null;
        if (userWantsMicRef.current) resumeRecognition();
      };

      await audio.play();
    } catch {
      isPlayingAudioRef.current = false;
      if (userWantsMicRef.current) resumeRecognition();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent.id]);

  // ─── Speech Recognition ────────────────────────────────────────

  const startRecognition = useCallback(() => {
    const SpeechRecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognitionClass) return;
    if (recognitionRef.current) return;
    if (isPlayingAudioRef.current) return;

    const recognition = new SpeechRecognitionClass();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      if (isPlayingAudioRef.current) return;

      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          const text = result[0].transcript.trim();
          if (text) {
            setInterimText('');
            if (loadingRef.current) {
              // Queue it — will send when current response finishes
              pendingVoiceRef.current = text;
            } else {
              doSend(text, true);
            }
          }
        } else {
          interim += result[0].transcript;
        }
      }
      if (interim) setInterimText(interim);
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      if (userWantsMicRef.current && !isPlayingAudioRef.current) {
        setTimeout(() => {
          if (userWantsMicRef.current && !isPlayingAudioRef.current) {
            startRecognition();
          }
        }, 100);
      } else {
        setIsListening(false);
      }
    };

    recognition.onerror = (event: { error: string }) => {
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      console.error('[stt] Error:', event.error);
    };

    try {
      recognition.start();
      setIsListening(true);
    } catch {
      recognitionRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopRecognition = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    setIsListening(false);
    setInterimText('');
  }, []);

  const pauseRecognition = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.abort();
      recognitionRef.current = null;
      setIsListening(false);
    }
  }, []);

  const resumeRecognition = useCallback(() => {
    if (userWantsMicRef.current && !isPlayingAudioRef.current) {
      startRecognition();
    }
  }, [startRecognition]);

  const toggleMic = useCallback(() => {
    if (isListening) {
      userWantsMicRef.current = false;
      stopRecognition();
      setVoiceMode(false);
    } else {
      userWantsMicRef.current = true;
      setVoiceMode(true);
      if (!isPlayingAudioRef.current) {
        startRecognition();
      }
    }
  }, [isListening, startRecognition, stopRecognition]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.onend = null;
        recognitionRef.current.abort();
      }
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
      }
    };
  }, []);

  // ─── Stream a single agent response (reusable helper) ──────────

  const pendingAttachmentsRef = useRef<Array<{ type: 'image' | 'file'; name: string; mimeType: string; data: string }>>([]);

  const streamAgentResponse = async (
    targetAgent: Agent,
    text: string,
    history: Array<{ role: string; content: string }>,
    signal: AbortSignal,
  ): Promise<string> => {
    // Add streaming placeholder for this agent
    setMessages(prev => [
      ...prev,
      { role: 'assistant', content: '', streaming: true, agentId: targetAgent.id, agentName: targetAgent.name, agentEmoji: targetAgent.avatarEmoji, agentColor: targetAgent.accentColor },
    ]);

    const res = await fetch(`${API_BASE_URL}/v1/agents/${targetAgent.id}/chat`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        message: text,
        history,
        ...(chatSessionId ? { chatSessionId } : {}),
        ...(voiceModeRef.current ? { voiceMode: true } : {}),
        ...(brandId ? { brandId } : {}),
        ...(pendingAttachmentsRef.current.length > 0 ? { attachments: pendingAttachmentsRef.current } : {}),
      }),
      signal,
    });

    if (!res.ok) throw new Error(`Request failed: ${res.status}`);

    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let accumulated = '';
    let displayed = '';
    let typewriterTimer: ReturnType<typeof setInterval> | null = null;
    let thinkingSound: { start: () => void; stop: () => void } | null = null;

    // Typewriter: gradually reveal accumulated text ~60 chars/sec
    const startTypewriter = () => {
      if (typewriterTimer) return;
      typewriterTimer = setInterval(() => {
        if (displayed.length < accumulated.length) {
          // Release characters in bursts (9 chars per 50ms tick ≈ same reveal
          // speed as 3 chars per 16ms, with ~3x fewer re-renders)
          const charsToAdd = Math.min(9, accumulated.length - displayed.length);
          displayed = accumulated.slice(0, displayed.length + charsToAdd);
          setMessages(prev => {
            const next = [...prev];
            const last = next[next.length - 1];
            next[next.length - 1] = { ...last, content: displayed, streaming: true };
            return next;
          });
        } else if (typewriterTimer) {
          // Caught up — pause until more text arrives
        }
      }, 50); // ~20fps
    };

    const stopTypewriter = () => {
      if (typewriterTimer) { clearInterval(typewriterTimer); typewriterTimer = null; }
    };

    // ─── Sentence-level TTS queue ─────────────────────────────
    const ttsQueue: Promise<void>[] = [];
    let spokenSoFar = ''; // tracks what we've already sent to TTS
    interface AudioQueueItem { audio: HTMLAudioElement; sentence: string; }
    const audioQueue: AudioQueueItem[] = [];
    let isPlayingQueue = false;
    let voiceRevealed = ''; // text revealed in sync with audio in voice mode

    const playNextInQueue = () => {
      if (audioQueue.length === 0) {
        isPlayingQueue = false;
        isPlayingAudioRef.current = false;
        currentAudioRef.current = null;
        // In voice mode, reveal any remaining text and mark streaming done
        if (voiceModeRef.current) {
          voiceRevealed = accumulated;
          displayed = accumulated;
          setMessages(prev => {
            const next = [...prev];
            const last = next[next.length - 1];
            next[next.length - 1] = { ...last, content: accumulated, streaming: false };
            return next;
          });
        }
        if (userWantsMicRef.current) setTimeout(() => resumeRecognition(), 300);
        return;
      }
      const item = audioQueue.shift()!;
      currentAudioRef.current = item.audio;
      isPlayingAudioRef.current = true;

      // Stop thinking sound when first audio plays
      if (thinkingSound) { thinkingSound.stop(); thinkingSound = null; }

      // In voice mode: reveal this sentence's text when audio starts playing
      if (voiceModeRef.current) {
        voiceRevealed += item.sentence;
        displayed = voiceRevealed;
        setMessages(prev => {
          const next = [...prev];
          const last = next[next.length - 1];
          next[next.length - 1] = { ...last, content: displayed, streaming: true };
          return next;
        });
      }

      item.audio.onended = playNextInQueue;
      item.audio.onerror = playNextInQueue;
      item.audio.play().catch(playNextInQueue);
    };

    // Serialize TTS API calls to avoid rate limiting (max 1 concurrent)
    let ttsChain = Promise.resolve();

    const enqueueSentenceTTS = (sentence: string) => {
      if (!voiceModeRef.current || !sentence.trim()) return;
      ttsChain = ttsChain.then(() =>
        coworkApi.tts.generate(targetAgent.id, sentence.trim())
          .then(({ audioUrl }) => {
            const audio = new Audio(coworkApi.tts.audioSrc(audioUrl));
            audioQueue.push({ audio, sentence });
            if (!isPlayingQueue) {
              isPlayingQueue = true;
              pauseRecognition();
              playNextInQueue();
            }
          })
          .catch(() => {}) // swallow TTS errors
      );
      ttsQueue.push(ttsChain);
    };

    // Sentence boundary detector: split on . ! ? followed by space or end
    const flushSentences = () => {
      const unspoken = accumulated.slice(spokenSoFar.length);
      // Match complete sentences (ending with . ! ? followed by space or end)
      const sentenceMatch = unspoken.match(/^([\s\S]*?[.!?])(?:\s|$)/);
      if (sentenceMatch) {
        const sentence = sentenceMatch[1];
        spokenSoFar += sentence;
        // Also consume trailing whitespace
        const trailing = unspoken.slice(sentence.length).match(/^\s*/);
        if (trailing) spokenSoFar += trailing[0];
        enqueueSentenceTTS(sentence);
        // Recursively check for more sentences
        flushSentences();
      }
    };

    // Captured here (not thrown inside processLine) so the parse-error catch
    // below can't swallow it — rethrown after the read loop to reach the
    // sendMessage error handler that renders it in the chat.
    let streamError: Error | null = null;
    const processLine = (line: string) => {
      if (!line.startsWith('data: ')) return;
      try {
        const data = JSON.parse(line.slice(6));
        if (data.event === 'voice_thinking') {
          // Deep thinking mode — play subtle ambient sound while model works
          if (voiceModeRef.current) {
            thinkingSound = createThinkingSound();
            thinkingSound.start();
          }
        } else if (data.event === 'text_delta') {
          // Don't stop thinking sound here — let it play until first audio chunk starts
          // Fix missing spaces between sentences (e.g. "word.Next" → "word. Next")
          let chunk = data.text as string;
          if (accumulated.length > 0 && chunk.length > 0) {
            const lastChar = accumulated[accumulated.length - 1];
            const firstChar = chunk[0];
            if (/[.!?]/.test(lastChar) && /[A-Z]/.test(firstChar)) {
              chunk = ' ' + chunk;
            }
          }
          accumulated += chunk;
          startTypewriter();
          // In voice mode: DON'T send individual sentences to TTS
          // We'll generate a voice summary after the full response
          if (!voiceModeRef.current) {
            flushSentences();
          }
        } else if (data.event === 'content_proposed') {
          setContentCards(prev => [...prev, data as ContentCardData]);
        } else if (data.event === 'image_generated') {
          setGeneratedImages(prev => [...prev, data as GeneratedImage]);
        } else if (data.event === 'document_generated') {
          const docData = data as GeneratedDocument;
          setGeneratedDocuments(prev => [...prev, docData]);
          // Scroll to bottom after a moment so the download card is visible
          setTimeout(() => {
            const chatContainer = document.querySelector('[data-chat-scroll]');
            if (chatContainer) chatContainer.scrollTop = chatContainer.scrollHeight;
          }, 200);
        } else if (data.event === 'task_created') {
          setCreatedTasks(prev => [...prev, data as TaskCreatedInfo]);
        } else if (data.event === 'dashboard_created') {
          setCreatedDashboards(prev => [...prev, data as DashboardCreatedInfo]);
          setTimeout(() => {
            const chatContainer = document.querySelector('[data-chat-scroll]');
            if (chatContainer) chatContainer.scrollTop = chatContainer.scrollHeight;
          }, 200);
        } else if (data.event === 'approval_requested') {
          setApprovalCards(prev => [...prev, {
            approvalId: data.approvalId,
            type: data.type,
            title: data.title,
            details: data.details,
            resolvedStatus: null,
          }]);
        } else if (data.event === 'tool_progress') {
          setMessages(prev => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last && last.streaming) {
              const activities = [...(last.toolActivities ?? [])];
              const existing = activities.findIndex(a => a.tool === data.tool);
              if (existing >= 0) {
                activities[existing] = { ...activities[existing], status: data.status ?? 'running' };
              } else {
                activities.push({ tool: data.tool, label: data.label, input: data.input, status: data.status ?? 'running' });
              }
              next[next.length - 1] = { ...last, toolLabel: data.label, toolActivities: activities };
            }
            return next;
          });
        } else if (data.event === 'suggested_replies') {
          setSuggestedReplies(data.suggestions ?? []);
        } else if (data.event === 'done') {
          if (data.chatSessionId) setChatSessionId(data.chatSessionId);
        } else if (data.event === 'error') {
          streamError = new Error(data.message);
        }
      } catch { /* ignore parse errors */ }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) processLine(line);
      if (streamError) throw streamError;
    }

    // Flush remaining buffer (may contain the final `done` event)
    buffer += decoder.decode();
    for (const line of buffer.split('\n')) processLine(line);
    if (streamError) throw streamError;

    // Stop thinking sound + typewriter and flush remaining text
    if (thinkingSound) { (thinkingSound as { stop: () => void }).stop(); thinkingSound = null; }
    stopTypewriter();

    // Show full text immediately (in voice mode, audio plays summary separately)
    displayed = accumulated;
    setMessages(prev => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last && last.streaming) {
        next[next.length - 1] = { ...last, content: accumulated || last.content, streaming: false };
      }
      return next;
    });

    // Voice mode: generate a conversational summary and speak THAT (not the full text)
    if (voiceModeRef.current && accumulated.length > 0) {
      try {
        // Ask the backend for a voice summary of the full response
        const summaryRes = await fetch(`${API_BASE_URL}/v1/tts/voice-summary`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            agentId: targetAgent.id,
            fullText: accumulated,
            agentName: targetAgent.name,
          }),
        });
        if (summaryRes.ok) {
          const { summary } = await summaryRes.json();
          if (summary) {
            // Split summary into sentences and TTS each one
            const sentences = summary.match(/[^.!?]+[.!?]+/g) || [summary];
            for (const s of sentences) {
              enqueueSentenceTTS(s.trim());
            }
          }
        }
      } catch { /* If summary fails, just show text without voice */ }

      // Wait for all TTS to finish
      await Promise.all(ttsQueue);
      if (isPlayingQueue) {
        await new Promise<void>(resolve => {
          const check = () => {
            if (!isPlayingQueue) { resolve(); return; }
            setTimeout(check, 200);
          };
          check();
        });
      }

      // Once audio is done, make sure full text is revealed
      setMessages(prev => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last && last.role === 'assistant') {
          next[next.length - 1] = { ...last, content: accumulated, streaming: false };
        }
        return next;
      });
    }

    return accumulated;
  };

  // ─── Send message ──────────────────────────────────────────────

  const doSend = async (text: string, fromVoice = false) => {
    if ((!text && pendingFiles.length === 0) || loadingRef.current) return;
    setInput('');
    setSuggestedReplies([]);

    // Convert pending files to base64 attachments
    const attachments: Array<{ type: 'image' | 'file'; name: string; mimeType: string; data: string }> = [];
    for (const pf of pendingFiles) {
      const data = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1]); // base64 without prefix
        reader.readAsDataURL(pf.file);
      });
      attachments.push({
        type: pf.file.type.startsWith('image/') ? 'image' : 'file',
        name: pf.file.name,
        mimeType: pf.file.type,
        data,
      });
    }
    setPendingFiles([]);
    pendingAttachmentsRef.current = attachments;

    const history = messages.map(m => ({ role: m.role, content: m.content }));

    // Add user message with attachment info
    const displayText = attachments.length > 0
      ? `${text}${text ? '\n' : ''}${attachments.map(a => `📎 ${a.name}`).join('\n')}`
      : text;
    setMessages(prev => [...prev, { role: 'user', content: displayText, fromVoice }]);
    setLoading(true);
    loadingRef.current = true;

    abortRef.current = new AbortController();

    try {
      if (isCallMode && callAgents.length > 1) {
        // ─── Multi-agent call mode: auto-route via conductor ───

        // Fire filler audio immediately if voice input (runs in parallel with routing)
        const fillerPromise = fromVoice
          ? playFiller(callAgents[0].id, text)
          : Promise.resolve();

        const routingHistory = messages.slice(-8).map(m => ({
          role: m.role,
          agentId: m.agentId,
          agentName: m.agentName,
          content: m.content,
        }));

        const routing = await coworkApi.callRouting.route(
          text,
          callAgents.map(a => a.id),
          routingHistory,
        );

        const primaryAgent = callAgents.find(a => a.id === routing.primaryAgentId) ?? callAgents[0];

        // Wait for filler to finish before streaming real response
        await fillerPromise;

        // Stream primary agent response
        const primaryResponse = await streamAgentResponse(
          primaryAgent,
          text,
          history,
          abortRef.current.signal,
        );

        // Only bring in a secondary agent if the conductor explicitly picked one
        if (routing.secondaryAgentId) {
          const secondaryAgent = callAgents.find(a => a.id === routing.secondaryAgentId);
          if (secondaryAgent) {
            const updatedHistory = [
              ...history,
              { role: 'user' as const, content: text },
              { role: 'assistant' as const, content: primaryResponse },
            ];

            const followUpPrompt = `The user said: "${text}"\n\n${primaryAgent.name} just responded. Only chime in if you have something genuinely valuable to add from your expertise as ${secondaryAgent.title}. Do NOT repeat what was already said. If you have nothing meaningful to add, respond with just: "[no addition needed]"`;

            const secondaryResponse = await streamAgentResponse(
              secondaryAgent,
              followUpPrompt,
              updatedHistory,
              abortRef.current.signal,
            );

            // If agent indicated nothing to add, remove their message
            if (secondaryResponse.trim().toLowerCase().includes('[no addition needed]')) {
              setMessages(prev => prev.slice(0, -1));
            }
          }
        }
      } else {
        // ─── Single agent mode (normal chat or 1-agent call) ───
        // Play filler for voice input in call mode
        if (isCallMode && fromVoice) {
          await playFiller(agent.id, text);
        }
        await streamAgentResponse(agent, text, history, abortRef.current.signal);
      }
      pendingAttachmentsRef.current = [];
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setMessages(prev => {
        const next = [...prev];
        // Find the last assistant message to mark as error, or append one
        const lastIdx = next.length - 1;
        if (lastIdx >= 0 && next[lastIdx].role === 'assistant') {
          next[lastIdx] = {
            ...next[lastIdx],
            content: friendlyChatError(err),
            streaming: false,
          };
        } else {
          next.push({
            role: 'assistant',
            content: friendlyChatError(err),
            streaming: false,
          });
        }
        return next;
      });
    } finally {
      setLoading(false);
      loadingRef.current = false;

      // Process any queued voice message
      if (pendingVoiceRef.current) {
        const queued = pendingVoiceRef.current;
        pendingVoiceRef.current = null;
        setTimeout(() => doSend(queued, true), 200);
      }
    }
  };

  const handleApproval = async (approvalId: string, status: 'approved' | 'rejected') => {
    try {
      await coworkApi.approvals.resolve(approvalId, status);
      setApprovalCards(prev => prev.map(a =>
        a.approvalId === approvalId ? { ...a, resolvedStatus: status } : a
      ));
    } catch (err) {
      console.error('Failed to resolve approval:', err);
    }
  };

  const sendMessage = async (text = input.trim()) => {
    doSend(text);
  };

  // Auto-send initial message from home page (wait for history restoration first)
  const initialMessageSentRef = useRef(false);
  useEffect(() => {
    if (initialMessage && !initialMessageSentRef.current && !restoringHistory) {
      initialMessageSentRef.current = true;
      sendMessage(initialMessage);
    }
  }, [initialMessage, restoringHistory]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const reset = () => {
    abortRef.current?.abort();
    setMessages([]);
    setLoading(false);
    loadingRef.current = false;
    setSuggestedReplies([]);
    setContentCards([]);
    setGeneratedImages([]);
    setGeneratedDocuments([]);
    setCreatedDashboards([]);
    setCreatedTasks([]);
    setChatSessionId(null);
  };

  return (
    <div className="flex flex-col h-full bg-[#f8f9fa]">
      {openPresentationId && (
        <PresentationModal presentationId={openPresentationId} onClose={() => setOpenPresentationId(null)} />
      )}

      {/* ── Top bar ───────────────────────────────── */}
      <div className="shrink-0 flex items-center gap-3 px-6 py-3.5 bg-white border-b border-gray-100">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-[12px] text-[#98a2b3] hover:text-[#344054] transition-colors mr-1"
          style={{ fontWeight: 500 }}
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back
        </button>

        <div className="w-px h-4 bg-gray-100" />

        {/* Voice call: live badge + timer */}
        {isVoiceCall && (
          <>
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 border border-green-200">
              <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              <span className="text-[10px] text-green-700 font-semibold">Live</span>
            </div>
            <span className="text-[11px] text-[#98a2b3] font-mono">{callMM}:{callSS}</span>
          </>
        )}

        {/* Active agent avatar + name */}
        <AgentAvatar
          name={agent.name}
          avatarUrl={agent.avatarUrl}
          emoji={agent.avatarEmoji}
          gradientFrom={agent.gradientFrom}
          gradientTo={agent.gradientTo}
          accentColor={agent.accentColor}
          className="w-7 h-7 rounded-lg shrink-0"
        />

        <div className="flex items-baseline gap-2">
          <span className="text-[13px] text-[#101828]" style={{ fontWeight: 700 }}>
            {agent.name}
          </span>
          <span className="text-[11px] text-[#c4c9d4]">{plainTitle(agent.title)}</span>
        </div>

        {/* Voice mode indicator */}
        {isListening && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 border border-green-200">
            <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
            <span className="text-[10px] text-green-700 font-semibold">Listening</span>
          </div>
        )}

        <div className="flex-1" />

        {/* Call mode: participant avatar stack */}
        {isCallMode && (
          <div className="flex items-center -space-x-2 mr-2">
            {callAgents.map(a => (
              <div key={a.id} className="relative" title={a.name}>
                <AgentAvatar
                  name={a.name}
                  avatarUrl={a.avatarUrl}
                  emoji={a.avatarEmoji}
                  gradientFrom={a.gradientFrom}
                  gradientTo={a.gradientTo}
                  className="w-7 h-7 rounded-lg border-2 border-white"
                />
              </div>
            ))}
          </div>
        )}

        {/* Voice mode toggle */}
        <button
          onClick={() => setVoiceMode(!voiceMode)}
          title={voiceMode ? 'Voice responses ON' : 'Voice responses OFF'}
          className={cn(
            'flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg transition-all',
            voiceMode
              ? 'bg-purple-50 text-purple-600 border border-purple-200'
              : 'text-[#c4c9d4] hover:text-[#667085] hover:bg-gray-50'
          )}
          style={{ fontWeight: 500 }}
        >
          {voiceMode ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
          {voiceMode ? 'Voice ON' : 'Voice OFF'}
        </button>

        {messages.length > 0 && !isCallMode && (
          <button
            onClick={reset}
            title="New chat"
            className="flex items-center gap-1.5 text-[11px] text-[#c4c9d4] hover:text-[#667085] transition-colors px-2.5 py-1.5 rounded-lg hover:bg-gray-50"
          >
            <RotateCcw className="w-3 h-3" />
            New chat
          </button>
        )}

        {/* Voice call: End call button (text chats leave via Back) */}
        {isVoiceCall && (
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-[12px] font-semibold transition-colors"
          >
            <PhoneOff className="w-3.5 h-3.5" />
            End
          </button>
        )}
      </div>

      {/* ── Main area ──────────────────────────────── */}
      <div className="flex-1 flex min-h-0 overflow-hidden relative">

      {/* ── Messages column (with floating input) ── */}
      <div className="flex-1 relative">
      <div className="absolute inset-0 overflow-y-auto" data-chat-scroll>
        <div className="max-w-[640px] mx-auto px-4 pt-6 pb-[180px]">

          {/* Restoring history — lightweight spinner instead of the new-chat hero */}
          {restoringHistory && messages.length === 0 && (
            <div className="flex items-center justify-center pt-24 pb-10">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: agent.accentColor }} />
            </div>
          )}

          {/* Empty state */}
          {!restoringHistory && messages.length === 0 && (
            <div className="flex flex-col items-center text-center pt-12 pb-10">
              <AgentAvatar
                name={agent.name}
                avatarUrl={agent.avatarUrl}
                emoji={agent.avatarEmoji}
                gradientFrom={agent.gradientFrom}
                gradientTo={agent.gradientTo}
                accentColor={agent.accentColor}
                className="w-14 h-14 rounded-2xl mb-5 shadow-sm"
                textClassName="text-2xl"
              />

              <h2 className="text-[20px] text-[#101828] mb-1" style={{ fontWeight: 700 }}>
                Hi, I'm {agent.name}
              </h2>
              <p className="text-[14px] text-[#98a2b3] max-w-[340px] mb-6 leading-relaxed">
                {plainTagline(agent.tagline)}
              </p>

              <div className="flex flex-wrap gap-1.5 justify-center mb-10">
                {agent.personalityTraits.map(t => (
                  <span
                    key={t}
                    className="text-[10px] px-2.5 py-1 rounded-full"
                    style={{ background: `${agent.accentColor}12`, color: agent.accentColor, fontWeight: 600 }}
                  >
                    {plainTitle(t)}
                  </span>
                ))}
              </div>

              {/* Suggested prompts (none for agents whose chat is just an input) */}
              {prompts.length > 0 && <div className="w-full space-y-2">
                {prompts.map(p => (
                  <button
                    key={p}
                    onClick={() => sendMessage(p)}
                    className="w-full text-left px-4 py-3 rounded-xl bg-white border border-gray-100 text-[13px] text-[#344054] hover:border-gray-200 hover:shadow-sm transition-all"
                    style={{ fontWeight: 500 }}
                  >
                    {p}
                  </button>
                ))}
              </div>}
            </div>
          )}

          {/* Message list */}
          <div className="space-y-6">
            {messages.map((msg, i) => {
              // In call mode, resolve the agent that sent this message
              const msgAgent = isCallMode && msg.agentId
                ? callAgents.find(a => a.id === msg.agentId) ?? agent
                : agent;
              const msgColor = msg.agentColor ?? msgAgent.accentColor;

              return (
              <div key={i} className={cn('flex gap-3 group', msg.role === 'user' ? 'justify-end' : 'justify-start')}>

                {/* Agent avatar */}
                {msg.role === 'assistant' && (
                  <AgentAvatar
                    name={msgAgent.name}
                    avatarUrl={msgAgent.avatarUrl}
                    emoji={msg.agentEmoji ?? msgAgent.avatarEmoji}
                    gradientFrom={msgAgent.gradientFrom}
                    gradientTo={msgAgent.gradientTo}
                    accentColor={msgColor}
                    className="w-7 h-7 rounded-lg shrink-0 mt-0.5"
                  />
                )}

                {/* Bubble */}
                {msg.role === 'user' ? (
                  (() => {
                    const isFromSlack = msg.content?.startsWith('[slack] ');
                    const displayContent = isFromSlack ? msg.content.slice(8) : msg.content;
                    return (
                      <div className="flex items-start gap-1.5 max-w-[75%]">
                        {msg.fromVoice && (
                          <Mic className="w-3 h-3 shrink-0 mt-3" style={{ color: `${msgColor}80` }} />
                        )}
                        {isFromSlack && (
                          <div className="w-4 h-4 shrink-0 mt-2.5 rounded flex items-center justify-center" title="Sent from Slack" style={{ background: '#611f69' }}>
                            <span className="text-[8px] text-white font-bold">S</span>
                          </div>
                        )}
                        <div
                          className="px-4 py-2.5 rounded-2xl rounded-tr-sm text-[13px] leading-relaxed"
                          style={{ background: msgColor, color: '#fff' }}
                        >
                          {displayContent}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div className="flex-1 min-w-0">
                    {/* Agent name label in call mode */}
                    {isCallMode && (
                      <span className="text-[11px] font-bold mb-1 block" style={{ color: msgColor }}>
                        {msg.agentName ?? msgAgent.name}
                      </span>
                    )}
                    {msg.content ? (
                      <div className={`chat-markdown${msg.streaming ? ' streaming' : ''}`}>
                        {msg.streaming ? (
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              a: ({ href, children, ...props }) => (
                                <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                  {children}
                                </a>
                              ),
                            }}
                          >{msg.content}</ReactMarkdown>
                        ) : (
                          <MemoMarkdown content={msg.content} />
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1.5 py-1">
                        {/* Tool activity trace */}
                        {msg.toolActivities && msg.toolActivities.length > 0 ? (
                          <div className="space-y-1">
                            {msg.toolActivities.map((ta, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2 text-[11px] px-2.5 py-1.5 rounded-lg bg-[#f9fafb] border border-gray-100"
                              >
                                {ta.status === 'running' ? (
                                  <Loader2 className="w-3 h-3 animate-spin shrink-0" style={{ color: msgColor }} />
                                ) : ta.status === 'done' ? (
                                  <Check className="w-3 h-3 text-green-500 shrink-0" />
                                ) : (
                                  <span className="w-3 h-3 text-red-400 shrink-0">!</span>
                                )}
                                <span className="text-[#344054] truncate" style={{ fontWeight: 500 }}>{ta.label}</span>
                                {ta.input && (
                                  <span className="text-[#c4c9d4] truncate text-[10px]">{ta.input}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <ThinkingIndicator color={msgColor} agentName={msg.agentName} />
                        )}
                      </div>
                    )}
                    {msg.streaming && msg.content && (
                      <span
                        className="inline-block w-0.5 h-4 ml-0.5 animate-pulse rounded-full align-text-bottom"
                        style={{ background: msgColor }}
                      />
                    )}
                    {/* Feedback buttons — visible on hover for completed assistant messages */}
                    {!msg.streaming && msg.content && (
                      <FeedbackButtons
                        agentId={msgAgent.id}
                        messageContent={msg.content}
                        messageIndex={i}
                        accentColor={msgColor}
                      />
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {/* Interim voice text */}
          {interimText && (
            <div className="flex justify-end mt-4">
              <div className="flex items-center gap-1.5 max-w-[75%]">
                <Mic className="w-3 h-3 text-gray-400 shrink-0 animate-pulse" />
                <div className="px-4 py-2.5 rounded-2xl rounded-tr-sm text-[13px] leading-relaxed bg-gray-100 text-gray-400 italic">
                  {interimText}
                </div>
              </div>
            </div>
          )}

          {/* Content cards */}
          {contentCards.length > 0 && (
            <div className="space-y-3 mt-4 pl-10">
              {contentCards.map(card => (
                <ContentCard key={card.contentId} card={card} onSave={onContentSaved} />
              ))}
            </div>
          )}

          {/* Generated images (from Jones Art Director) */}
          {generatedImages.length > 0 && (
            <div className="space-y-3 mt-4 pl-10">
              {generatedImages.map(img => (
                <ImageCard key={img.contentId} image={img} onSave={onContentSaved} />
              ))}
            </div>
          )}

          {/* Generated documents */}
          {generatedDocuments.length > 0 && (
            <div className="space-y-3 mt-4 pl-10">
              {generatedDocuments.map(doc => (
                <div
                  key={doc.contentId}
                  className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
                  style={{ maxWidth: 360 }}
                >
                  <div className="flex items-center gap-3 px-4 py-3">
                    <span className="text-2xl">{DOC_ICONS[doc.format] ?? '📄'}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-[#101828] truncate" style={{ fontWeight: 600 }}>
                        {doc.title}
                      </div>
                      <div className="text-[11px] text-[#98a2b3]">
                        {DOC_LABELS[doc.format] ?? doc.format.toUpperCase()} document
                      </div>
                    </div>
                    {doc.presentationId ? (
                      <button
                        onClick={() => setOpenPresentationId(doc.presentationId!)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] bg-[#be43ff] text-white hover:bg-[#a835e0] transition-colors shrink-0 cursor-pointer"
                        style={{ fontWeight: 600 }}
                      >
                        Open
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (!doc.documentUrl) return;
                          fetch(`${API_BASE_URL}${doc.documentUrl}`)
                            .then(res => res.blob())
                            .then(blob => {
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `${doc.title}.${doc.format}`;
                              document.body.appendChild(a);
                              a.click();
                              setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(a); }, 100);
                            })
                            .catch(() => window.open(`${API_BASE_URL}${doc.documentUrl}`, '_blank'));
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] bg-[#be43ff] text-white hover:bg-[#a835e0] transition-colors shrink-0 cursor-pointer"
                        style={{ fontWeight: 600 }}
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Created dashboard cards */}
          {createdDashboards.length > 0 && (
            <div className="space-y-3 mt-4 pl-10">
              {createdDashboards.map(db => (
                <div
                  key={db.dashboardId}
                  className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden cursor-pointer hover:border-[#be43ff]/30 transition-colors"
                  style={{ maxWidth: 360 }}
                  onClick={() => onViewDashboard?.(db.dashboardId)}
                >
                  <div className="flex items-center gap-3 px-4 py-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#be43ff]/10 to-[#2e024c]/10 flex items-center justify-center shrink-0">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#be43ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-[#101828] truncate" style={{ fontWeight: 600 }}>
                        {db.title}
                      </div>
                      <div className="text-[11px] text-[#98a2b3]">
                        Live dashboard &middot; {db.type}
                      </div>
                    </div>
                    <span
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] bg-[#be43ff] text-white shrink-0"
                      style={{ fontWeight: 600 }}
                    >
                      View
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Created task cards previously rendered here — moved to the right
              sidebar's "Tasks" panel to keep the chat clean. The agent's text
              response remains the inline marker that work was created. */}

          {/* Approval cards */}
          {approvalCards.length > 0 && (
            <div className="space-y-2 mt-4 pl-10">
              {approvalCards.map(approval => (
                <div key={approval.approvalId} className="mt-3 border border-amber-200 bg-amber-50 rounded-lg p-4" style={{ maxWidth: 420 }}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-amber-600 font-medium text-sm uppercase tracking-wide">
                      {approval.type.replace(/_/g, ' ')}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      approval.resolvedStatus === 'approved' ? 'bg-green-100 text-green-700' :
                      approval.resolvedStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {approval.resolvedStatus === 'approved' ? 'Approved' : approval.resolvedStatus === 'rejected' ? 'Rejected' : 'Pending'}
                    </span>
                  </div>
                  <h4 className="font-semibold text-gray-900">{approval.title}</h4>
                  {approval.details && <p className="text-sm text-gray-600 mt-1">{approval.details}</p>}
                  {!approval.resolvedStatus && (
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleApproval(approval.approvalId, 'approved')}
                        className="px-4 py-1.5 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700"
                      >
                        Approve
                      </button>
                      <button
                        onClick={() => handleApproval(approval.approvalId, 'rejected')}
                        className="px-4 py-1.5 bg-red-100 text-red-700 rounded-md text-sm font-medium hover:bg-red-200"
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Suggested reply buttons */}
          {suggestedReplies.length > 0 && !loading && !noChips && (
            <div className="flex flex-wrap gap-2 mt-4 pl-10">
              {suggestedReplies.map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(suggestion)}
                  className="px-3.5 py-2 rounded-xl text-[12px] bg-white border border-gray-200 text-[#344054] hover:border-gray-300 hover:shadow-sm transition-all leading-snug"
                  style={{ fontWeight: 500 }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          <div ref={bottomRef} className="h-[200px] shrink-0" />
        </div>
      </div>
      {/* Input bar is injected here by the floating overlay below */}
      </div>{/* close messages column */}

      {/* ── Right sidebar — Progress + Tasks + Brand context + OKR Progress ── */}
      {!isCallMode && (
        <div className="w-[260px] shrink-0 border-l border-gray-100 bg-white flex flex-col">
          {/* Progress section */}
          <div className="px-4 py-3.5 border-b border-gray-100">
            <h3 className="text-[11px] text-[#98a2b3] font-semibold uppercase tracking-wider">Progress</h3>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3">
            {(() => {
              // Collect all tool activities from the latest streaming message
              const lastMsg = messages[messages.length - 1];
              const activities = lastMsg?.streaming ? (lastMsg.toolActivities ?? []) : [];
              const isStreaming = lastMsg?.streaming ?? false;

              // Build step list: always show the base steps + any tool calls
              const steps: Array<{ label: string; status: 'done' | 'running' | 'pending' }> = [];

              if (messages.length > 0) {
                // Base pipeline steps
                steps.push({ label: 'Loading brand memory', status: isStreaming || !lastMsg?.streaming ? 'done' : 'pending' });
                steps.push({ label: 'Assembling context', status: isStreaming || !lastMsg?.streaming ? 'done' : 'pending' });

                if (isStreaming) {
                  if (activities.length === 0 && !lastMsg?.content) {
                    steps.push({ label: 'Thinking...', status: 'running' });
                  } else if (activities.length === 0 && lastMsg?.content) {
                    steps.push({ label: 'Generating response', status: 'running' });
                  }
                } else if (messages.length > 1) {
                  steps.push({ label: 'Response complete', status: 'done' });
                }

                // Tool activities
                for (const ta of activities) {
                  steps.push({
                    label: ta.label || ta.tool,
                    status: ta.status === 'done' ? 'done' : ta.status === 'error' ? 'done' : 'running',
                  });
                }
              }

              if (steps.length === 0) {
                return (
                  <p className="text-[11px] text-[#c4c9d4] text-center py-4">
                    Send a message to see agent progress
                  </p>
                );
              }

              return (
                <div className="space-y-0">
                  {steps.map((step, i) => (
                    <div key={i} className="flex items-start gap-2.5 relative">
                      {/* Vertical line */}
                      {i < steps.length - 1 && (
                        <div
                          className="absolute left-[7px] top-[18px] w-px bottom-0"
                          style={{ background: step.status === 'done' ? '#be43ff30' : '#e5e7eb' }}
                        />
                      )}
                      {/* Status icon */}
                      <div className="shrink-0 mt-0.5 relative z-10">
                        {step.status === 'done' ? (
                          <div className="w-[15px] h-[15px] rounded-full border-2 border-[#be43ff] flex items-center justify-center">
                            <Check className="w-2 h-2 text-[#be43ff]" />
                          </div>
                        ) : step.status === 'running' ? (
                          <div className="w-[15px] h-[15px] rounded-full border-2 border-[#be43ff] flex items-center justify-center">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#be43ff] animate-pulse" />
                          </div>
                        ) : (
                          <div className="w-[15px] h-[15px] rounded-full border-2 border-gray-200" />
                        )}
                      </div>
                      <span
                        className={`text-[11px] py-1 leading-tight ${
                          step.status === 'done' ? 'text-[#344054]'
                          : step.status === 'running' ? 'text-[#be43ff] font-medium'
                          : 'text-[#c4c9d4]'
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Tasks section — moved from inline chat to keep conversation clean */}
          <div className="border-t border-gray-100">
            <div className="px-4 py-3.5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-[11px] text-[#98a2b3] font-semibold uppercase tracking-wider">Tasks</h3>
              {createdTasks.length > 0 && (
                <span className="text-[10px] text-[#94a3b8] font-medium">{createdTasks.length}</span>
              )}
            </div>
            <div className="max-h-[280px] overflow-y-auto">
              {createdTasks.length === 0 ? (
                <p className="text-[11px] text-[#c4c9d4] px-4 py-3 leading-tight">
                  Tasks the agent creates in this conversation will show here.
                </p>
              ) : (
                <div className="px-2 py-2 space-y-1">
                  {createdTasks.map((t) => {
                    const status = t.status || 'queue';
                    const isDone = status === 'done';
                    const isFailed = status === 'failed' || status === 'stopped';
                    const isWorking = status === 'working' || status === 'queue';
                    return (
                      <div
                        key={t.taskId}
                        className={`rounded-lg border px-2.5 py-2 transition-colors ${
                          isDone ? 'border-green-200 bg-green-50/50'
                          : isFailed ? 'border-red-200 bg-red-50/50'
                          : 'border-[#be43ff]/15 bg-[#faf5ff]/60'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {isWorking ? (
                            <Loader2 className="w-3.5 h-3.5 mt-0.5 text-[#be43ff] animate-spin shrink-0" />
                          ) : isDone ? (
                            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-green-500 shrink-0" />
                          ) : isFailed ? (
                            <Circle className="w-3.5 h-3.5 mt-0.5 text-red-400 shrink-0" />
                          ) : (
                            <Circle className="w-3.5 h-3.5 mt-0.5 text-gray-400 shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-[11.5px] text-[#101828] leading-snug" style={{ fontWeight: 600 }}>
                              {t.title}
                            </p>
                            <p className="text-[10px] text-[#94a3b8] mt-0.5 truncate">
                              {t.agentName || t.assignedAgentName || 'Agent'}
                              {' · '}
                              <span className={isDone ? 'text-green-600' : isFailed ? 'text-red-600' : 'text-[#be43ff]'}>
                                {isDone ? 'done' : isFailed ? 'failed' : isWorking ? 'working' : 'queued'}
                              </span>
                            </p>
                            {/* Inline deliverable open / download */}
                            {isDone && t.deliverableUrl && t.deliverableType === 'presentation' && (
                              <button
                                onClick={() => setOpenPresentationId(t.deliverableUrl!)}
                                className="mt-1.5 flex items-center gap-1 text-[10.5px] text-green-700 hover:underline"
                                style={{ fontWeight: 600 }}
                              >
                                <FileText className="w-3 h-3" />
                                Open {t.deliverableTitle || 'Presentation'}
                              </button>
                            )}
                            {isDone && t.deliverableUrl && t.deliverableType !== 'presentation' && (
                              <button
                                onClick={() => {
                                  const src = t.deliverableUrl!;
                                  const download = (blob: Blob, name: string) => {
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = name;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                  };
                                  // Text deliverables carry their content inline, not a URL —
                                  // save directly instead of fetching a garbage address.
                                  if (!src.startsWith('/') && !src.startsWith('http')) {
                                    download(new Blob([src], { type: 'text/markdown' }), `${t.deliverableTitle || 'deliverable'}.md`);
                                    return;
                                  }
                                  fetch(src.startsWith('http') ? src : `${API_BASE_URL}${src}`, { headers: authHeaders(false) })
                                    .then((r) => r.blob())
                                    .then((blob) => download(blob, t.deliverableTitle || 'deliverable'))
                                    .catch((err) => console.error('[chat] deliverable download failed:', err));
                                }}
                                className="mt-1.5 flex items-center gap-1 text-[10.5px] text-green-700 hover:underline"
                                style={{ fontWeight: 600 }}
                              >
                                <FileText className="w-3 h-3" />
                                Download
                              </button>
                            )}
                          </div>
                          {onNavigateToTasks && (
                            <button
                              onClick={() => onNavigateToTasks(t.taskId)}
                              className="text-[#cbd5e1] hover:text-[#be43ff] transition-colors p-0.5"
                              title="Open in Task Board"
                            >
                              <ArrowRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Brand context section */}
          <div className="border-t border-gray-100">
            <div className="px-4 py-3.5 border-b border-gray-100">
              <h3 className="text-[11px] text-[#98a2b3] font-semibold uppercase tracking-wider">Brand Context</h3>
            </div>
            <div className="px-4 py-3 space-y-2">
              <div className="flex items-center gap-2 text-[11px] text-[#667085]">
                <div className="w-4 h-4 rounded-full border border-gray-200 flex items-center justify-center">
                  <Check className="w-2 h-2 text-green-500" />
                </div>
                Brand Memory
              </div>
              <div className="flex items-center gap-2 text-[11px] text-[#667085]">
                <div className="w-4 h-4 rounded-full border border-gray-200 flex items-center justify-center">
                  <Check className="w-2 h-2 text-green-500" />
                </div>
                Agent Memory
              </div>
            </div>
          </div>

          {/* OKR Progress (account-manager only) */}
          {agent.id === 'account-manager' && okrs.length > 0 && (
            <div className="border-t border-gray-100">
              <div className="px-4 py-3.5 border-b border-gray-100">
                <h3 className="text-[11px] text-[#98a2b3] font-semibold uppercase tracking-wider">OKR Progress</h3>
              </div>
              <div className="px-4 py-3 space-y-2.5">
                {okrs.map(okr => (
                  <div key={okr.id}>
                    <div className="text-[11px] font-medium text-[#344054] truncate">{okr.title}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-green-500 rounded-full" style={{ width: `${Math.round(okr.progress * 100)}%` }} />
                      </div>
                      <span className="text-[10px] text-[#98a2b3]">{Math.round(okr.progress * 100)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}

      {/* ── Call mode: Participant sidebar ── */}
      {isCallMode && (
        <div className="w-[220px] shrink-0 border-l border-gray-100 bg-white flex flex-col">
          <div className="px-4 py-3.5 border-b border-gray-100">
            <h3 className="text-[12px] text-[#667085] font-semibold uppercase tracking-wider">{isVoiceCall ? 'In this call' : 'In this chat'}</h3>
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-2">
            {/* User row */}
            <div className="flex items-center gap-3 px-2 py-2.5 rounded-xl mb-1">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#be43ff]/15 to-[#be43ff]/5 flex items-center justify-center text-sm border border-[#be43ff]/15 shrink-0">
                You
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-[#101828] font-semibold">You</div>
                {isVoiceCall && <div className="text-[10px] text-[#98a2b3]">{isListening ? 'Speaking' : 'Muted'}</div>}
              </div>
              <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${isListening ? 'bg-green-500' : 'bg-gray-300'}`} />
            </div>

            {/* Agent rows */}
            {callAgents.map(a => {
              // Determine live state from messages: is this agent currently streaming?
              const lastMsg = messages.length > 0 ? messages[messages.length - 1] : null;
              const isSpeaking = lastMsg?.streaming && lastMsg?.agentId === a.id;
              const isThinking = loading && !isSpeaking && lastMsg?.agentId === a.id && !lastMsg?.content;
              const stateLabel = isSpeaking ? 'Speaking...' : isThinking ? 'Thinking...' : a.title;
              const stateActive = isSpeaking || isThinking;
              return (
                <div
                  key={a.id}
                  className={cn(
                    'w-full flex items-center gap-3 px-2 py-2.5 rounded-xl mb-1 transition-all text-left',
                    stateActive ? 'bg-gray-50 ring-1 ring-gray-200' : ''
                  )}
                >
                  <div className="relative shrink-0">
                    <AgentAvatar
                      name={a.name}
                      avatarUrl={a.avatarUrl}
                      emoji={a.avatarEmoji}
                      gradientFrom={a.gradientFrom}
                      gradientTo={a.gradientTo}
                      accentColor={a.accentColor}
                      className="w-9 h-9 rounded-xl"
                      textClassName="text-base"
                    />
                    {/* Speaking waveform mini */}
                    {isSpeaking && (
                      <div className="absolute -bottom-1 -right-1 flex items-end gap-[2px] h-3 px-1 py-0.5 bg-black/60 rounded-full">
                        {[0, 1, 2].map(i => (
                          <div
                            key={i}
                            className="w-[2px] rounded-full"
                            style={{
                              background: a.accentColor,
                              animation: `callWaveform 0.5s ease-in-out ${i * 0.1}s infinite alternate`,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-[#101828] font-semibold truncate">{a.name}</div>
                    <div className="text-[10px] text-[#98a2b3] truncate">
                      {stateActive ? (
                        <span style={{ color: a.accentColor, fontWeight: 600 }}>{stateLabel}</span>
                      ) : (
                        a.title
                      )}
                    </div>
                  </div>
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                    isSpeaking ? 'bg-green-500'
                    : isThinking ? 'bg-yellow-400 animate-pulse'
                    : 'bg-gray-300'
                  }`} />
                </div>
              );
            })}
          </div>
          <div className="px-4 py-3 border-t border-gray-100">
            <div className="text-[10px] text-[#c4c9d4]">
              {callAgents.length} agent{callAgents.length !== 1 ? 's' : ''} {isVoiceCall ? `in call · ${callMM}:${callSS}` : 'in chat'}
            </div>
          </div>
        </div>
      )}

      {/* Waveform animation for call mode */}
      {isCallMode && (
        <style>{`
          @keyframes callWaveform {
            from { height: 3px; }
            to { height: 10px; }
          }
        `}</style>
      )}

      </div>{/* end flex main area */}

      {/* Typing dots animation */}
      <style>{`
        @keyframes typingDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-6px); opacity: 1; }
        }
        .typing-dot {
          animation: typingDot 1.4s ease-in-out infinite;
        }
      `}</style>

      {/* ── Input bar — floating overlay inside messages column only ── */}
      <div className="absolute bottom-0 left-0 z-20 px-4 pb-5 pt-4 bg-[#f8f9fa]" style={{ right: isCallMode ? '0' : '220px' }}>
        <div className="max-w-[640px] mx-auto">
          {/* Pending file previews */}
          {pendingFiles.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2 px-1">
              {pendingFiles.map((pf, i) => (
                <div key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-[12px] text-[#344054]">
                  {pf.file.type.startsWith('image/') && pf.preview ? (
                    <img src={pf.preview} alt="" className="w-6 h-6 rounded object-cover" />
                  ) : pf.file.type.startsWith('audio/') ? (
                    <Music className="w-3.5 h-3.5 text-[#667085]" />
                  ) : pf.file.type === 'application/pdf' ? (
                    <FileText className="w-3.5 h-3.5 text-red-400" />
                  ) : (
                    <FileText className="w-3.5 h-3.5 text-[#667085]" />
                  )}
                  <span className="truncate max-w-[120px]">{pf.file.name}</span>
                  <button onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))} className="text-[#98a2b3] hover:text-[#ef4444]">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div
            className="flex items-center gap-3 bg-white rounded-2xl border border-gray-200 px-4 py-3 shadow-md transition-all"
            style={{ '--focus-color': `${agent.accentColor}50` } as React.CSSProperties}
          >
            {/* File upload button */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf,audio/*,.doc,.docx,.txt,.csv,.xlsx"
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                const newFiles = files.map(file => {
                  const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
                  return { file, preview, type: file.type };
                });
                setPendingFiles(prev => [...prev, ...newFiles]);
                e.target.value = '';
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              title="Attach files"
              className="w-8 h-8 rounded-xl flex items-center justify-center bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600 transition-all shrink-0"
            >
              <Paperclip className="w-3.5 h-3.5" />
            </button>

            {/* Mic button */}
            <button
              onClick={toggleMic}
              title={isListening ? 'Stop listening' : 'Start voice input'}
              className={cn(
                'w-8 h-8 rounded-xl flex items-center justify-center transition-all shrink-0',
                isListening
                  ? 'bg-green-500 text-white shadow-sm shadow-green-500/30'
                  : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              )}
            >
              {isListening ? <Mic className="w-3.5 h-3.5" /> : <MicOff className="w-3.5 h-3.5" />}
            </button>

            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isListening ? 'Listening... or type here' : `Message ${agent.name}…`}
              rows={1}
              disabled={loading}
              className="flex-1 resize-none bg-transparent text-[13px] text-[#101828] placeholder:text-[#c4c9d4] outline-none leading-relaxed disabled:opacity-50 py-0.5"
            />
            <button
              onClick={() => sendMessage()}
              disabled={(!input.trim() && pendingFiles.length === 0) || loading}
              className="w-8 h-8 rounded-xl flex items-center justify-center transition-all shrink-0 disabled:opacity-30 hover:opacity-90"
              style={{ background: agent.accentColor }}
            >
              {loading
                ? <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                : <Send className="w-3.5 h-3.5 text-white" />
              }
            </button>
          </div>
          <p className="text-center text-[10px] text-[#d0d5dd] mt-2">
            {isListening
              ? 'Voice mode active · Speak naturally · Click mic to stop'
              : 'Enter to send · Shift + Enter for new line · Click mic for voice'}
          </p>
        </div>
      </div>
    </div>
  );
}

/** Force-download a URL as a file */
async function forceDownload(url: string, filename: string) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  } catch { window.open(url, '_blank'); }
}

/* ── Inline image card for visuals generated by Jones ── */
function ImageCard({ image, onSave }: { image: GeneratedImage; onSave?: () => void }) {
  const [savedToQueue, setSavedToQueue] = useState(false);
  const imgUrl = `${API_BASE_URL}${image.imageUrl}`;
  const filename = `${image.title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}.png`;

  const handleSave = async () => {
    try {
      await fetch(`${API_BASE_URL}/v1/content-queue/${image.contentId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ status: 'saved' }),
      });
      setSavedToQueue(true);
      onSave?.();
    } catch { /* ignore */ }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm" style={{ maxWidth: 400 }}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-50">
        <span
          className="text-[10px] px-2 py-0.5 rounded-full"
          style={{ background: '#8b5cf615', color: '#8b5cf6', fontWeight: 600 }}
        >
          Visual
        </span>
        <span className="text-[12px] text-[#344054] truncate" style={{ fontWeight: 600 }}>
          {image.title}
        </span>
      </div>

      <div className="p-2">
        <div className="relative rounded-lg overflow-hidden bg-gray-50">
          <img
            src={`${API_BASE_URL}${image.imageUrl}`}
            alt={image.title}
            className="w-full h-auto object-contain"
            style={{ maxHeight: 400 }}
          />
          <button
            onClick={() => forceDownload(imgUrl, filename)}
            className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1.5 px-4 py-2.5 border-t border-gray-50">
        {savedToQueue ? (
          <span className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg text-green-600 bg-green-50" style={{ fontWeight: 500 }}>
            <Check className="w-3 h-3" />
            Saved!
          </span>
        ) : (
          <button
            onClick={handleSave}
            className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg text-[#667085] hover:bg-gray-50 transition-all"
            style={{ fontWeight: 500 }}
          >
            <Bookmark className="w-3 h-3" />
            Save
          </button>
        )}
        <button
          onClick={() => forceDownload(imgUrl, filename)}
          className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg text-[#667085] hover:bg-gray-50 transition-all"
          style={{ fontWeight: 500 }}
        >
          <Download className="w-3 h-3" />
          Download
        </button>
      </div>
    </div>
  );
}
